import { useEffect, useState } from 'react';
import Modal from '@/components/Modal';
import { Button } from '@/components/Button';
import { apiGetRequest, apiPostRequest } from '@/utils/apiClient';
import { detectDeviceAndBrowser, getDeviceBrowserLabel } from '@/utils/deviceAndBrowser';
import { getAppType } from '@/utils/loginInfo';
import { useTranslation } from 'react-i18next';

const FEEDBACK_CODE = 'NEW_REGISTRATION';

const DEFAULT_OPTION_KEYS = [
    'feedback.options.optionA',
    'feedback.options.optionB',
    'feedback.options.optionC',
    'feedback.options.optionD',
    'feedback.options.optionE',
    'feedback.options.optionF',
    'feedback.options.optionG',
];

type FeedbackTemplateItems = {
    options?: boolean;
    comment?: boolean;
};

type FeedbackTemplateResponse = {
    feedbackCode: string;
    version: number;
    items?: FeedbackTemplateItems;
};

type RegistrationFeedbackModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

const RegistrationFeedbackModal = ({ isOpen, onClose }: RegistrationFeedbackModalProps) => {
    const { t, i18n } = useTranslation('onboarding');

    const [template, setTemplate] = useState<FeedbackTemplateResponse | null>(null);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    const resetState = () => {
        setTemplate(null);
        setSelectedOptions([]);
        setIsLoading(false);
        setIsSubmitting(false);
        setLoadError(null);
        setSubmitError(null);
        setValidationError(null);
    };

    useEffect(() => {
        if (!isOpen) {
            resetState();
            return;
        }

        const loadTemplate = async () => {
            setIsLoading(true);
            setLoadError(null);
            try {
                const localeParam = encodeURIComponent(i18n.language ?? '');
                const response = await apiGetRequest(
                    `/api/feedback/templates/${FEEDBACK_CODE}?locale=${localeParam}`,
                );
                if (!response.ok) {
                    throw new Error('failed to load template');
                }
                const data: FeedbackTemplateResponse = await response.json();
                setTemplate(data);
                if (data.items?.options === false) {
                    // もし選択肢が無効なら、そのまま送信フェーズに進む
                    void handleSubmit(data, []);
                }
            } catch (error) {
                setLoadError(t('feedback.loadError'));
            } finally {
                setIsLoading(false);
            }
        };

        void loadTemplate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, i18n.language]);

    const toggleOption = (optionKey: string) => {
        setSelectedOptions((prev) => {
            if (prev.includes(optionKey)) {
                return prev.filter((key) => key !== optionKey);
            }
            return [...prev, optionKey];
        });
    };

    const handleSubmit = async (
        templateResponse: FeedbackTemplateResponse | null,
        overrideSelected?: string[],
    ) => {
        const currentSelected = overrideSelected ?? selectedOptions;
        const optionsEnabled = templateResponse?.items?.options ?? true;
        if (optionsEnabled && currentSelected.length === 0) {
            setValidationError(t('feedback.validation.optionRequired'));
            return;
        }
        setValidationError(null);
        setSubmitError(null);
        setIsSubmitting(true);

        try {
            if (templateResponse) {
                const translatedOptions = currentSelected.map((key) => t(key));
                const response = await apiPostRequest('/api/feedback/submit', {
                    feedbackCode: templateResponse.feedbackCode ?? FEEDBACK_CODE,
                    templateVersion: templateResponse.version,
                    selectedOptions: translatedOptions,
                    comment: null,
                    deviceBrowser: getDeviceBrowserLabel(detectDeviceAndBrowser()),
                    appType: getAppType(),
                    locale: i18n.language,
                });
                if (!response.ok) {
                    throw new Error('feedback submit failed');
                }
            }
            onClose();
        } catch (error) {
            setSubmitError(t('feedback.submitError'));
            setIsSubmitting(false);
        }
    };

    const handlePrimarySubmit = () => {
        void handleSubmit(template);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => undefined}
            contentClassName="w-full max-w-2xl p-8"
            closeButtonClassName="hidden"
            disableCloseOnEsc={true}
            disableCloseOnOverlay={true}
        >
            <div className="space-y-6">
                <div className="text-center space-y-2">
                    <h2 className="text-xl font-semibold">{t('feedback.title')}</h2>
                    <p className="text-sm text-gray-600">{t('feedback.description')}</p>
                </div>

                {isLoading && (
                    <p className="text-sm text-gray-500 text-center">{t('feedback.loading')}</p>
                )}

                {!isLoading && loadError && (
                    <div className="space-y-4 text-center">
                        <p className="text-sm text-red-600">{loadError}</p>
                    </div>
                )}

                {!isLoading && !loadError && template?.items?.options !== false && (
                    <div className="space-y-4">
                        <p className="text-sm text-gray-700 font-medium text-center">
                            {t('feedback.optionLabel')}
                        </p>
                        <div className="space-y-2">
                            {DEFAULT_OPTION_KEYS.map((optionKey) => {
                                const label = t(optionKey);
                                return (
                                    <label
                                        key={optionKey}
                                        className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-blue-400 transition cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            className="mt-1 h-4 w-4"
                                            checked={selectedOptions.includes(optionKey)}
                                            onChange={() => toggleOption(optionKey)}
                                            disabled={isSubmitting}
                                        />
                                        <span className="text-sm text-gray-700">{label}</span>
                                    </label>
                                );
                            })}
                        </div>
                        {validationError && (
                            <p className="text-sm text-red-600 text-center">{validationError}</p>
                        )}
                    </div>
                )}

                {submitError && <p className="text-sm text-red-600 text-center">{submitError}</p>}

                <div className="flex justify-center pt-2">
                    <Button
                        variant="original"
                        onClick={handlePrimarySubmit}
                        isLoading={isSubmitting}
                        loadingLabel={t('feedback.sending', { ns: 'onboarding' })}
                        className="w-auto px-12"
                    >
                        {t('feedback.sendButton')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default RegistrationFeedbackModal;
